import {
  HOSPITALITY_SUBTYPES,
  INDUSTRIAL_SUBTYPES,
  LAND_SUBTYPES,
  MULTIFAMILY_SUBTYPES,
  OFFICE_SUBTYPES,
  OTHER_SUBTYPES,
  RETAIL_SUBTYPES,
} from '~/utils'

export const MAP_PROPERTY_SUBTYPE_GROUPS = {
  Hospitality: HOSPITALITY_SUBTYPES,
  Industrial: INDUSTRIAL_SUBTYPES,
  Land: LAND_SUBTYPES,
  Multifamily: MULTIFAMILY_SUBTYPES,
  Office: OFFICE_SUBTYPES,
  Retail: RETAIL_SUBTYPES,
  Other: OTHER_SUBTYPES,
} as const

export type MapPropertyType = keyof typeof MAP_PROPERTY_SUBTYPE_GROUPS

type PropertyTypeFind = {
  propertyTypes: { $in: string[] }
}

type ScopedPropertyTypeFind = {
  $and: [PropertyTypeFind, { propertySubtypes: { $in: string[] } }]
}

type PropertyUseClause = {
  $or: (PropertyTypeFind | ScopedPropertyTypeFind)[]
}

const PROPERTY_SUBTYPE_SCOPE_SEPARATOR = '::'

const propertySubtypeOwners = Object.entries(MAP_PROPERTY_SUBTYPE_GROUPS).reduce(
  (owners, [propertyType, propertySubtypes]) => {
    propertySubtypes.forEach((propertySubtype) => {
      owners[propertySubtype] ??= []
      owners[propertySubtype].push(propertyType as MapPropertyType)
    })

    return owners
  },
  {} as Record<string, MapPropertyType[]>
)

export function encodeMapPropertySubtypeValue(
  propertyType: MapPropertyType,
  propertySubtype: string
) {
  return `${propertyType}${PROPERTY_SUBTYPE_SCOPE_SEPARATOR}${propertySubtype}`
}

export function decodeMapPropertySubtypeValue(value: string): {
  propertySubtype: string
  propertyType?: MapPropertyType
} {
  const [maybePropertyType, ...rest] = value.split(
    PROPERTY_SUBTYPE_SCOPE_SEPARATOR
  )

  if (rest.length === 0 || !(maybePropertyType in MAP_PROPERTY_SUBTYPE_GROUPS)) {
    return { propertySubtype: value }
  }

  return {
    propertyType: maybePropertyType as MapPropertyType,
    propertySubtype: rest.join(PROPERTY_SUBTYPE_SCOPE_SEPARATOR),
  }
}

export function getMapPropertySubtypeOptions(propertyTypes: string[]) {
  return propertyTypes.flatMap((propertyType) => {
    if (!(propertyType in MAP_PROPERTY_SUBTYPE_GROUPS)) {
      return []
    }

    return MAP_PROPERTY_SUBTYPE_GROUPS[propertyType as MapPropertyType].map(
      (propertySubtype) => ({
        value: encodeMapPropertySubtypeValue(
          propertyType as MapPropertyType,
          propertySubtype
        ),
        label: propertySubtype,
        group: propertyType,
      })
    )
  })
}

export function getMapPropertySubtypeLabel(value: string) {
  return decodeMapPropertySubtypeValue(value).propertySubtype
}

function isSubtypeOwnedByPropertyType(
  propertyType: string,
  propertySubtype: string
) {
  return (
    propertyType in MAP_PROPERTY_SUBTYPE_GROUPS &&
    MAP_PROPERTY_SUBTYPE_GROUPS[
      propertyType as MapPropertyType
    ].includes(propertySubtype)
  )
}

function getScopedPropertySubtypes(
  propertyTypes: string[],
  propertySubtypeFilters: string[]
) {
  const scopedPropertySubtypes = propertyTypes.reduce(
    (acc, propertyType) => {
      acc[propertyType] = []
      return acc
    },
    {} as Record<string, string[]>
  )

  propertySubtypeFilters.forEach((propertySubtypeFilter) => {
    const { propertySubtype, propertyType } =
      decodeMapPropertySubtypeValue(propertySubtypeFilter)

    if (propertyType) {
      if (propertyType in scopedPropertySubtypes) {
        scopedPropertySubtypes[propertyType].push(propertySubtype)
      }
      return
    }

    const owners = propertySubtypeOwners[propertySubtype] || []

    owners
      .filter((owner) => propertyTypes.includes(owner))
      .forEach((owner) => {
        scopedPropertySubtypes[owner].push(propertySubtype)
      })
  })

  Object.keys(scopedPropertySubtypes).forEach((propertyType) => {
    scopedPropertySubtypes[propertyType] = [
      ...new Set(scopedPropertySubtypes[propertyType]),
    ]
  })

  return scopedPropertySubtypes
}

export function normalizeMapPropertySubtypeFilters(
  propertyTypes?: string[],
  propertySubtypeFilters?: string[]
) {
  if (!propertyTypes || propertyTypes.length === 0) {
    return undefined
  }

  if (!propertySubtypeFilters || propertySubtypeFilters.length === 0) {
    return undefined
  }

  const normalized = propertySubtypeFilters.flatMap((propertySubtypeFilter) => {
    const { propertySubtype, propertyType } =
      decodeMapPropertySubtypeValue(propertySubtypeFilter)

    if (propertyType) {
      if (
        propertyTypes.includes(propertyType) &&
        isSubtypeOwnedByPropertyType(propertyType, propertySubtype)
      ) {
        return [encodeMapPropertySubtypeValue(propertyType, propertySubtype)]
      }

      return []
    }

    return propertyTypes
      .filter((selectedPropertyType) =>
        isSubtypeOwnedByPropertyType(selectedPropertyType, propertySubtype)
      )
      .map((selectedPropertyType) =>
        encodeMapPropertySubtypeValue(
          selectedPropertyType as MapPropertyType,
          propertySubtype
        )
      )
  })

  return normalized.length > 0 ? [...new Set(normalized)] : undefined
}

export function buildMapPropertyUseClause(
  propertyTypes?: string[],
  propertySubtypeFilters?: string[]
): {
  propertyTypes?: { $in: string[] }
  propertyUseClause?: PropertyUseClause
} {
  if (!propertyTypes || propertyTypes.length === 0) {
    return {}
  }

  if (!propertySubtypeFilters || propertySubtypeFilters.length === 0) {
    return {
      propertyTypes: { $in: propertyTypes },
    }
  }

  const scopedPropertySubtypes = getScopedPropertySubtypes(
    propertyTypes,
    propertySubtypeFilters
  )
  const hasScopedSubtypes = Object.values(scopedPropertySubtypes).some(
    (values) => values.length > 0
  )

  if (!hasScopedSubtypes) {
    return {
      propertyTypes: { $in: propertyTypes },
    }
  }

  return {
    propertyUseClause: {
      $or: propertyTypes.map((propertyType) => {
        const propertySubtypes = scopedPropertySubtypes[propertyType]

        if (!propertySubtypes || propertySubtypes.length === 0) {
          return {
            propertyTypes: { $in: [propertyType] },
          }
        }

        return {
          $and: [
            { propertyTypes: { $in: [propertyType] } },
            { propertySubtypes: { $in: propertySubtypes } },
          ],
        }
      }),
    },
  }
}
